import React, { ReactElement, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { profileImage } from '../../assets';
import Seo from '../../utils/seo';

const About: React.FC = (): ReactElement => {
  useEffect(() => {
    const quotes = [
      'Build for impact, not applause',
      'Keep it dead simple',
      'Fail fast, fail forward',
      'I am because we are. Ubuntu!',
    ];

    let qIndex = 0;

    const quoteText = document.getElementById('quote-text') as HTMLElement;

    let interval: number | undefined;

    if (quoteText) {
      quoteText.textContent = quotes[qIndex];

      interval = window.setInterval(() => {
        qIndex = (qIndex + 1) % quotes.length;

        quoteText.textContent = quotes[qIndex];
      }, 4000);
    }

    return () => {
      if (interval !== undefined) {
        clearInterval(interval);
      }
    };
  }, []);

  return (
    <>
      <Seo
        pageTitle="About Oluwasegun Adepoju | Software Engineer"
        description="Learn about Oluwasegun Adepoju, a software engineer shaping human-centered digital products, product architecture, and thoughtful user experiences."
        path="/about"
      />
      <section className="page_view">
        <div className="page_inner">
          <div className="centreBody">
            <section className="about-container">
              <div className="about-inner">
                {/* Left column — intro & portrait */}
                <div className="about-left">
                  <div className="about-photo">
                    <img src={profileImage} alt="Oluwasegun Adepoju portrait" />
                  </div>

                  <div className="about-summary">
                    <h1>Oluwasegun Adepoju</h1>
                    <h2>Software Artist & Creative Thinker</h2>
                    <p>
                      Passionate about intuitive digital experiences that
                      connect people, solve problems, and make technology feel
                      human.
                    </p>

                    <br />
                    <br />

                    {/* Quote Rotator */}
                    <div className="rotating-quote now-info">
                      <span className="mantra-label">Mantra:</span>
                      <span className="quote-text fade">
                        <p id="quote-text">“Build for impact, not applause.”</p>
                      </span>
                    </div>
                    <br />

                    {/* Now Card */}
                    <div className="now-info">
                      <div className="now-header">Now</div>
                      <ul>
                        <li>
                          🌱 <strong>Exploring:</strong> Quantum Computing /{' '}
                          Fuchsia
                        </li>
                        <li>
                          📖 <strong>Reading:</strong>{' '}
                          <em>The Courage to Be Disliked</em>
                        </li>
                        <li>
                          🌍 <strong>Location:</strong> Earth
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="about-right">
                  <div className="about-section elegant-block">
                    <h3>My Philosophy</h3>
                    <p>
                      I believe that the best software balances logic with
                      empathy. My goal is to craft experiences that not only{' '}
                      <strong>work</strong> beautifully but also{' '}
                      <strong>feel</strong> effortless. Thoughtful design
                      powered by clean engineering.
                    </p>
                  </div>

                  <div className="about-section">
                    <h3>Craft & Expertise</h3>
                    <p>
                      My work spans full-stack engineering, architecture design,
                      and human-centered UX thinking. I enjoy bringing abstract
                      ideas to life, from backend logic to the smallest
                      interface detail that delights the user.
                    </p>
                    <ul className="skills">
                      <li>Js / Py / Go</li>
                      <li>Architecture</li>
                      <li>Cloud Infrastructure (AWS, GCP, Azure)</li>
                      <li>Leadership</li>
                      <li>Blockchain</li>
                      <li>ML/NLP</li>
                      <li>Quantum Programing</li>
                    </ul>
                  </div>

                  <div className="about-section bulletted">
                    <h3>Core Values</h3>
                    <ul>
                      <li>
                        <span>Integrity</span>
                      </li>
                      <li>
                        <span>Empathy</span>
                      </li>
                      <li>
                        <span>Curiosity</span>
                      </li>
                      <li>
                        <span>Clarity</span>
                      </li>
                    </ul>
                  </div>

                  <div className="about-section">
                    <h3>Principles I Live By</h3>
                    <blockquote>
                      “Fail fast, fail forward”
                      <br />
                      “Keep it dead simple.”
                      <br />
                      “Build for impact, not applause.”
                      <br />
                      “I am because we are.” Ubuntu!
                    </blockquote>
                  </div>

                  <div className="about-section">
                    <h3>Inspiration</h3>
                    <p>
                      I draw inspiration from everyday people, their patterns,
                      needs, and hopes. Design, to me, is a reflection of how
                      deeply we listen. When I’m stuck, I turn to nature,
                      literature, or a good sport activity.
                    </p>
                  </div>

                  <div className="about-section">
                    <h3>Outside Work</h3>
                    <p>
                      When I’m not coding, you’ll find me in the gym putting my
                      muscles to work; mentoring young creatives, exploring
                      quiet corners of the world, or journaling new ideas for
                      social impact.
                    </p>
                  </div>

                  <div className="about-section">
                    <h3>Future Vision</h3>
                    <p>
                      I’m fascinated by the intersection of technology and
                      social empathy how tools can empower communities and
                      create opportunities for connection and growth. I’m
                      currently exploring projects that apply technology to
                      unconventional human needs.
                    </p>
                  </div>

                  <div className="about-section closing">
                    <p>
                      I’m open to meaningful collaborations and conversations
                      that explore where creativity meets technology. Let’s
                      create something remarkable together.
                    </p>{' '}
                    <Link className="about_explorations_link" to="/work">
                      Browse my Explorations
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden="true"
                      >
                        <line x1="5" y1="12" x2="19" y2="12" />
                        <polyline points="12 5 19 12 12 19" />
                      </svg>
                    </Link>{' '}
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </section>
    </>
  );
};

export default About;
